import { Button, IconButton } from '@material-ui/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleCheck,
  faCircleExclamation,
  faClock,
  faMinusCircle,
  faSpinner,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import React from 'react';
import styled from 'styled-components';
import { ModalWrapper } from 'components/ModalWrapper';
import { HealthCheckDefinition } from 'services/health';
import { HealthCheckState } from 'types/health';

export type HealthCheckDisplayMode = 'table' | 'icon';

interface Props {
  check: HealthCheckDefinition;
  state: HealthCheckState | undefined;
  onRetry: (checkId: string) => void;
  mode?: HealthCheckDisplayMode;
  title?: string;
}

type StatusTone = 'neutral' | 'info' | 'success' | 'warning' | 'error';

// Icons/dots need a lighter amber than the chip TEXT: dark amber reads as red at small sizes.
const WARNING_ICON_COLOR = '#f59e0b';

const toneColors: Record<StatusTone, { text: string; bg: string }> = {
  neutral: { text: '#4b5563', bg: '#e5e7eb' },
  info: { text: '#1d4ed8', bg: '#dbeafe' },
  success: { text: '#15803d', bg: '#dcfce7' },
  warning: { text: '#b45309', bg: '#fef3c7' },
  error: { text: '#b91c1c', bg: '#fee2e2' },
};

const HealthCheckStatus: React.FC<Props> = ({ check, state, onRetry, mode = 'table', title }) => {
  const current = state ?? { status: 'pending' };
  const metrics = current.progress?.metrics ?? [];

  const completionPercent = current.progress?.completionPercent ?? 0;
  const completionDisplay = `${completionPercent.toFixed(2)}%`;

  const progressStatus = current.progress?.status;
  const hasIssues = progressStatus !== undefined && !['complete', 'empty'].includes(progressStatus);
  const canRetry = current.status !== 'loading' && (current.status !== 'success' || hasIssues);
  const iconOpacity = progressStatus === 'empty' ? 0.4 : 1;
  const modalHeight = metrics.length > 0 ? 360 : 260;

  const tone: StatusTone = (() => {
    if (current.status === 'loading') {
      return 'info';
    }
    if (current.status === 'error') {
      return 'error';
    }
    if (current.status === 'success') {
      if (progressStatus === 'empty') {
        return 'neutral';
      }
      if (hasIssues) {
        return 'warning';
      }
      return 'success';
    }
    if (current.status === 'skipped') {
      return 'neutral';
    }
    return 'neutral';
  })();

  const mainIcon = (() => {
    if (current.status === 'loading') {
      return faSpinner;
    }
    if (current.status === 'error') {
      return faTriangleExclamation;
    }
    if (current.status === 'success') {
      if (progressStatus === 'empty') {
        return faMinusCircle;
      }
      if (hasIssues) {
        return faCircleExclamation;
      }
      return faCircleCheck;
    }
    if (current.status === 'skipped') {
      return faMinusCircle;
    }
    return faClock;
  })();

  const statusText = (() => {
    if (current.status === 'loading') {
      return 'Processing…';
    }
    if (current.status === 'error') {
      return 'Failed';
    }
    if (current.status === 'success') {
      switch (progressStatus) {
        case 'complete':
          return 'All clear';
        case 'empty':
          return 'No data';
        case 'missing':
          return 'Missing data';
        case 'in-progress':
          return 'In progress';
        case 'unsupported':
          return 'Unsupported';
        default:
          return hasIssues ? 'Needs attention' : 'All clear';
      }
    }
    if (current.status === 'skipped') {
      return 'Skipped';
    }
    return 'Queued';
  })();

  const formatMetricValue = (value: number | string) => {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value.toLocaleString();
    }
    return String(value);
  };

  const [open, setOpen] = React.useState(false);

  const handleOpen = () => {
    if (current.status !== 'loading' && current.status !== 'pending') {
      setOpen(true);
    }
  };

  const handleClose = () => setOpen(false);

  const handleRetry = () => {
    onRetry(check.id);
    setOpen(false);
  };

  const iconButton = (
    <IconButton
      size="small"
      onClick={handleOpen}
      disabled={current.status === 'loading' || current.status === 'pending'}
      aria-label={statusText}
    >
      <StatusIcon
        icon={mainIcon}
        spin={current.status === 'loading'}
        $color={tone === 'warning' ? WARNING_ICON_COLOR : toneColors[tone].text}
        $opacity={iconOpacity}
      />
    </IconButton>
  );

  const iconLabel = title ?? check.label;
  const canOpenModal = current.status !== 'loading' && current.status !== 'pending';

  return (
    <>
      {mode === 'table' ? (
        iconButton
      ) : (
        <IconTile
          $interactive={canOpenModal}
          onClick={canOpenModal ? handleOpen : undefined}
          role={canOpenModal ? 'button' : undefined}
          tabIndex={canOpenModal ? 0 : undefined}
          aria-disabled={!canOpenModal}
          onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
            if (!canOpenModal) {
              return;
            }
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              handleOpen();
            }
          }}
        >
          {iconButton}
          <IconTileLabel>{iconLabel}</IconTileLabel>
        </IconTile>
      )}
      {open && (
        <ModalWrapper title={check.label} onClose={handleClose} width={520} height={modalHeight}>
          <ModalHeader>
            <StatusBadge $tone={tone}>
              <BadgeIcon icon={mainIcon} spin={current.status === 'loading'} $opacity={iconOpacity} />
              {statusText}
            </StatusBadge>
            <CompletionBlock>
              <CompletionValue>{completionDisplay}</CompletionValue>
            </CompletionBlock>
          </ModalHeader>
          {metrics.length > 0 && (
            <StatsGrid>
              {metrics.map((item) => (
                <StatCard key={item.label}>
                  <StatLabel>{item.label}</StatLabel>
                  <StatValue>{formatMetricValue(item.value)}</StatValue>
                </StatCard>
              ))}
            </StatsGrid>
          )}
          {canRetry && (
            <ModalActions>
              <Button onClick={handleRetry} color="primary" variant="contained">
                Re-run check
              </Button>
            </ModalActions>
          )}
        </ModalWrapper>
      )}
    </>
  );
};

const IconTile = styled.div<{ $interactive: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border: 1px solid ${({ $interactive }) => ($interactive ? '#d1d5db' : '#e5e7eb')};
  border-radius: 12px;
  background-color: #ffffff;
  min-width: 220px;
  width: 100%;
  box-sizing: border-box;
  transition: border-color 0.2s ease;

  ${({ $interactive }) =>
    $interactive
      ? `cursor: pointer;
         &:hover {
           border-color: #2563eb;
         }

         &:focus-visible {
           outline: 2px solid #2563eb;
           outline-offset: 2px;
         }
      `
      : ''}
`;

const IconTileLabel = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  flex: 1;
  line-height: 1.4;
`;

const StatusIcon = styled(FontAwesomeIcon)<{ $color: string; $opacity: number }>`
  font-size: 16px;
  color: ${({ $color }) => $color};
  opacity: ${({ $opacity }) => $opacity};
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
`;

const StatusBadge = styled.span<{ $tone: StatusTone }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  color: ${({ $tone }) => toneColors[$tone].text};
  background-color: ${({ $tone }) => toneColors[$tone].bg};
`;

const BadgeIcon = styled(FontAwesomeIcon)<{ $opacity: number }>`
  font-size: 14px;
  opacity: ${({ $opacity }) => $opacity};
`;

const CompletionBlock = styled.div`
  text-align: right;
`;

const CompletionValue = styled.span`
  font-size: 22px;
  font-weight: 700;
  color: #111827;
  line-height: 1;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 12px;
`;

const StatCard = styled.div`
  background-color: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const StatLabel = styled.span`
  font-size: 12px;
  color: #6b7280;
`;

const StatValue = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: #111827;
`;

const ModalActions = styled.div`
  margin-top: 24px;
  display: flex;
  justify-content: flex-end;
`;

export default HealthCheckStatus;
